import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PlayCircle, CheckCircle2, Circle, ChevronLeft, Loader2, Video, Award, Check } from "lucide-react";

export default function CoursePlayer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [active, setActive] = useState(0);
  const [completed, setCompleted] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/learn/${slug}`).then(({ data }) => { setCourse(data); setCompleted(data.completed_lessons || []); }).catch(() => setError(true));
  }, [slug]);

  if (error) return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center gap-4">
      <p className="text-[#8A8D98] font-body">No tienes acceso a este curso.</p>
      <Button onClick={() => navigate("/dashboard")} className="bg-[#00E5FF] text-black rounded-md">Volver al panel</Button>
    </div>
  );
  if (!course) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#00E5FF] animate-spin" /></div>;

  const lessons = course.lessons || [];
  const current = lessons[active] || {};
  const total = lessons.length;
  const progress = total ? Math.round(completed.length / total * 100) : 0;
  const isDone = (id) => completed.includes(id);
  const allDone = total > 0 && completed.length >= total;

  const toggle = async (lessonId) => {
    try {
      const { data } = await api.post("/progress/toggle", { course_id: course.id, lesson_id: lessonId });
      setCompleted(data.completed_lessons);
    } catch { toast.error("No se pudo guardar el progreso"); }
  };

  const downloadCert = async () => {
    try {
      const res = await fetch(`${API}/certificate/${course.id}`, { credentials: "include" });
      if (!res.ok) { toast.error("Completa todas las lecciones primero"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `certificado-${course.slug}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("¡Certificado descargado!");
    } catch { toast.error("No se pudo descargar el certificado"); }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <div className="bg-[#0C0D12]/90 backdrop-blur-xl border-b border-white/10 px-6 h-14 flex items-center justify-between">
        <button data-testid="player-back-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 font-mono text-sm text-[#8A8D98] hover:text-white transition-colors">
          <ChevronLeft className="w-4 h-4" /> Panel
        </button>
        <span className="font-heading font-semibold text-sm truncate max-w-[40%]">{course.title}</span>
        <span className="font-mono text-xs text-[#525560]" data-testid="player-progress-text">{progress}% completado</span>
      </div>

      <div className="flex-1 grid lg:grid-cols-12">
        <div className="lg:col-span-9 bg-black">
          <div className="w-full max-h-[80vh] bg-black flex items-center justify-center">
            {current.video_url ? (
              <video key={current.id} data-testid="lesson-video" src={current.video_url} controls autoPlay className="w-full max-h-[80vh] bg-black" />
            ) : (
              <div className="h-[60vh] flex flex-col items-center justify-center text-[#525560]"><Video className="w-12 h-12 mb-3" /><p className="font-mono text-sm">Sin video disponible</p></div>
            )}
          </div>
          <div className="p-8 border-t border-white/10 flex items-start justify-between gap-6">
            <div>
              <h1 className="font-heading font-bold text-2xl mb-2">{current.title}</h1>
              <p className="font-body text-[#8A8D98]">{current.description}</p>
            </div>
            <Button data-testid="mark-complete-btn" onClick={() => toggle(current.id)}
              className={`shrink-0 rounded-md h-11 font-semibold ${isDone(current.id) ? "bg-[#00E6BA]/15 text-[#00E6BA] border border-[#00E6BA]/40 hover:bg-[#00E6BA]/20" : "bg-[#00E5FF] text-black hover:bg-white"}`}>
              {isDone(current.id) ? <><Check className="w-4 h-4 mr-2" /> Completada</> : "Marcar completada"}
            </Button>
          </div>
        </div>

        <div className="lg:col-span-3 bg-[#0C0D12] border-l border-white/10 flex flex-col">
          <div className="p-5 border-b border-white/10">
            <h3 className="font-mono text-xs uppercase tracking-widest text-[#525560] mb-3">Contenido</h3>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <div data-testid="progress-bar" className="h-full bg-[#00E5FF] transition-all duration-500" style={{ width: `${progress}%` }} />
            </div>
            <p className="font-mono text-xs text-[#8A8D98] mt-2">{completed.length}/{total} lecciones</p>
          </div>
          <div className="overflow-y-auto flex-1">
            {lessons.map((l, i) => (
              <div key={l.id} className={`border-b border-white/5 flex items-center ${i === active ? "bg-[#00E5FF]/10" : "hover:bg-white/5"}`}>
                <button data-testid={`player-lesson-${i}`} onClick={() => setActive(i)} className="flex-1 text-left px-5 py-4 flex items-start gap-3">
                  {i === active ? <PlayCircle className="w-5 h-5 text-[#00E5FF] shrink-0 mt-0.5" /> : <span className="shrink-0 mt-0.5">{isDone(l.id) ? <CheckCircle2 className="w-5 h-5 text-[#00E6BA]" /> : <Circle className="w-5 h-5 text-[#525560]" />}</span>}
                  <div>
                    <p className={`font-body text-sm ${i === active ? "text-[#00E5FF]" : "text-white"}`}>{l.title}</p>
                    <span className="font-mono text-xs text-[#525560]">{l.duration}</span>
                  </div>
                </button>
              </div>
            ))}
          </div>
          <div className="p-5 border-t border-white/10">
            <Button data-testid="download-cert-btn" onClick={downloadCert} disabled={!allDone}
              className={`w-full rounded-md h-11 font-semibold ${allDone ? "bg-[#00E5FF] text-black hover:bg-white" : "bg-white/5 text-[#525560] cursor-not-allowed"}`}>
              <Award className="w-4 h-4 mr-2" /> {allDone ? "Descargar certificado" : "Completa el curso"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
